import {
    Box,
    Button,
    Card,
    Stack,
    TextField,
    Typography,
    Alert
} from '@mui/material';
import { useState } from 'react';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import TrendingDownIcon from '@mui/icons-material/TrendingDown';
import axios from 'axios';

export default function ClientHardLimit() {
    const limitFields = [
        { key: 'dailybuylimit', label: 'Daily Buy Limit', icon: TrendingUpIcon },
        { key: 'dailyselllimit', label: 'Daily Sell Limit', icon: TrendingDownIcon }
    ];

    const [limits, setLimits] = useState({
        dailybuylimit: '',
        dailyselllimit: ''
    });
    const [errors, setErrors] = useState({});
    const [isSaving, setIsSaving] = useState(false);
    const [statusMessage, setStatusMessage] = useState('');
    const [statusType, setStatusType] = useState('');
    const [isLoading, setIsLoading] = useState(false);

    const handleChange = (key) => (event) => {
        const nextValue = event.target.value;
        if (nextValue !== '' && !/^[0-9]+$/.test(nextValue)) {
            return;
        }

        setLimits((prev) => ({ ...prev, [key]: nextValue }));
        setErrors((prev) => ({ ...prev, [key]: '' }));
        setStatusMessage('');
    };

    const validate = () => {
        const nextErrors = {};

        limitFields.forEach(({ key }) => {
            if (limits[key] === '') {
                nextErrors[key] = 'Required';
            }
        });

        setErrors(nextErrors);
        return Object.keys(nextErrors).length === 0;
    };

    const handleSubmit = async () => {
        if (!validate()) {
            return;
        }

        setIsSaving(true);
        setStatusMessage('');

        try {
            const payload = Object.fromEntries(
                Object.entries(limits).map(([key, value]) => [
                    key,
                    parseInt(value, 10)
                ])
            );

            const response = await axios.post(
                '/api/gas/eex/clienthardlimit',
                payload
            );

            setStatusMessage('✓ Hard limits saved successfully!');
            setStatusType('success');
        } catch (error) {
            setStatusMessage(
                error.response?.data?.message ||
                    '✗ Unable to save hard limits. Please try again.'
            );
            setStatusType('error');
        } finally {
            setIsSaving(false);
        }
    };

    return (
        <Box
            sx={{
                p: 3,
                maxWidth: '100%',
                width: '100%',
                background: 'linear-gradient(135deg, #f5f7fa 0%, #f0f4f8 100%)',
                borderRadius: 2
            }}
        >
            {/* Header */}
            <Box sx={{ mb: 4 }}>
                <Typography
                    variant="h5"
                    sx={{
                        fontWeight: 700,
                        background:
                            'linear-gradient(135deg, #0b9cfd 0%, #3DC3C1 100%)',
                        backgroundClip: 'text',
                        WebkitBackgroundClip: 'text',
                        WebkitTextFillColor: 'transparent',
                        mb: 1
                    }}
                >
                    Client Hard Limits
                </Typography>
            </Box>

            {/* Limit Fields Card */}
            <Card
                sx={{
                    p: 3,
                    mb: 3,
                    background: '#fff',
                    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.08)',
                    border: '1px solid #E8F0FE'
                }}
            >
                <Box
                    sx={{
                        mb: 1,
                        backgroundColor: '#cbf0e6',
                        p: 2,
                        borderRadius: 1
                    }}
                >
                    <Typography variant="body2" color="text.secondary">
                        Set daily buy and sell limits for client hard limits
                    </Typography>
                </Box>

                <Stack spacing={2.5}>
                    {limitFields.map(({ key, label, icon: IconComponent }) => (
                        <Box
                            key={key}
                            sx={{
                                display: 'flex',
                                alignItems: 'flex-start',
                                gap: 2.5,
                                p: 2,
                                backgroundColor: '#F8FCFF',
                                borderRadius: 1.5,
                                border: `1px solid ${
                                    errors[key] ? '#FFE0B2' : '#CBF0E6'
                                }`,
                                transition: 'all 0.3s ease',
                                '&:hover': {
                                    backgroundColor: errors[key]
                                        ? '#FFF3E0'
                                        : '#F0FBFF',
                                    borderColor: '#3DC3C1'
                                }
                            }}
                        >
                            <Box
                                sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    minWidth: '40px',
                                    height: '40px',
                                    borderRadius: '8px',
                                    backgroundColor: '#E0F2F1',
                                    color: '#00796B',
                                    flexShrink: 0
                                }}
                            >
                                <IconComponent sx={{ fontSize: '1.25rem' }} />
                            </Box>
                            <Box sx={{ flex: 1 }}>
                                <Typography
                                    sx={{
                                        fontWeight: 600,
                                        color: '#1a3a52',
                                        mb: 1,
                                        fontSize: '0.95rem'
                                    }}
                                >
                                    {label}
                                </Typography>
                                <TextField
                                    fullWidth
                                    value={limits[key]}
                                    onChange={handleChange(key)}
                                    placeholder="Enter limit amount"
                                    size="small"
                                    type="number"
                                    error={Boolean(errors[key])}
                                    helperText={errors[key] || ''}
                                    inputProps={{
                                        inputMode: 'numeric',
                                        min: 0
                                    }}
                                    sx={{
                                        '& .MuiOutlinedInput-root': {
                                            backgroundColor: '#fff',
                                            borderRadius: 1,
                                            transition: 'all 0.2s ease',
                                            '&:hover fieldset': {
                                                borderColor: '#3DC3C1'
                                            },
                                            '&.Mui-focused fieldset': {
                                                borderColor: '#0b9cfd',
                                                boxShadow: '0 0 0 2px #CBF0E6'
                                            }
                                        },
                                        '& .MuiOutlinedInput-input': {
                                            padding: '10px 12px',
                                            fontSize: '0.95rem'
                                        }
                                    }}
                                />
                            </Box>
                        </Box>
                    ))}
                </Stack>
            </Card>

            {/* Status Area */}
            {statusMessage && (
                <Alert
                    severity={statusType}
                    icon={
                        statusType === 'success' ? (
                            <CheckCircleIcon sx={{ color: '#4CAF50' }} />
                        ) : (
                            <ErrorIcon sx={{ color: '#F44336' }} />
                        )
                    }
                    sx={{
                        mb: 2,
                        backgroundColor:
                            statusType === 'success' ? '#E8F5E9' : '#FFEBEE',
                        color: statusType === 'success' ? '#2E7D32' : '#C62828',
                        border: `1px solid ${
                            statusType === 'success' ? '#C8E6C9' : '#FFCDD2'
                        }`
                    }}
                >
                    {statusMessage}
                </Alert>
            )}

            {/* Action Button */}
            <Box sx={{ display: 'flex', gap: 2 }}>
                <Button
                    variant="contained"
                    onClick={handleSubmit}
                    disabled={isSaving}
                    sx={{
                        backgroundColor: '#0b9cfd',
                        color: '#fff',
                        fontWeight: 600,
                        px: 3,
                        py: 1.2,
                        borderRadius: 1,
                        textTransform: 'none',
                        fontSize: '0.95rem',
                        transition: 'all 0.3s ease',
                        boxShadow: '0 4px 12px rgba(11, 156, 253, 0.3)',
                        '&:hover': {
                            transform: 'translateY(-2px)',
                            boxShadow: '0 6px 16px rgba(11, 156, 253, 0.4)'
                        },
                        '&:disabled': {
                            background: '#CCCCCC',
                            boxShadow: 'none'
                        },
                        marginLeft: 'auto'
                    }}
                >
                    {isSaving ? 'Saving...' : 'Save Hard Limits'}
                </Button>
            </Box>

            {/* Info Footer */}
            <Box
                sx={{
                    mt: 3,
                    p: 2,
                    backgroundColor: '#E0F2F1',
                    borderRadius: 1.5,
                    border: '1px solid #B2DFDB'
                }}
            >
                <Typography
                    variant="caption"
                    sx={{ color: '#00695c', lineHeight: 1.6 }}
                >
                    💡 <strong>Tip:</strong> Hard limits restrict the total
                    daily buy and sell amounts. These are stricter than trading
                    limits.
                </Typography>
            </Box>
        </Box>
    );
}
